import ClearIcon from '@clearkit/icons/glyphs/CloseOrb';
import MoreIcon from '@clearkit/icons/glyphs/Ellipsis';
import FolderIcon from '@clearkit/icons/glyphs/Folder';
import OpenFolderIcon from '@clearkit/icons/glyphs/FolderOpen';
import {
  CKField,
  CKFieldLeftElement,
  CKFieldRightElement,
} from '@clearkit/react';
import { Mutation } from 'react-apollo';
import Highlighter from 'react-highlight-words';
import { Link } from 'react-router-dom';

import ConfirmDeleteModal from '~/components/ConfirmDeleteModal';
import SegmentActionsMenu from '~/components/SegmentActionsMenu';
import ToastsContainer from '~/containers/Toasts';
import history from '~/lib/history';
import paths from '~/lib/paths';
import {
  segmentActionLocations,
  trackSegmentDeleted,
} from '~/lib/segmentAnalytics';

import CollectionName from './CollectionName';
import destroyCollectionMutation from './destroyCollection.graphql';
import MoreMenu from './MoreMenu';
import SegmentListIcon from './SegmentListIcon';
import styles from './styles.style';

class Collection extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selected: !this.getClosedState(),
      renaming: false,
      menuOpen: false,
      showDeleteModal: false,
    };
  }

  componentDidUpdate = (previousProps, previousState) => {
    if (previousProps.segmentId !== this.props.segmentId) {
      if (
        this.props.segments.map((s) => s.id).indexOf(this.props.segmentId) > -1
      ) {
        setTimeout(() => {
          this.segmentsDiv.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }, 10);
      }
    }
    if (previousState.selected !== this.state.selected) {
      this.setInitialClosedState(!this.state.selected);
    }
  };

  moreIcon = React.createRef();
  renameInput = React.createRef();
  segmentsDiv = React.createRef();

  segmentSelected = () => {
    const { segments, segmentId, addingSegment } = this.props;
    return segments.map((s) => s.id).indexOf(segmentId) > -1 || addingSegment;
  };

  handleDeleteCollectionError = () => {
    ToastsContainer.addError('There was an error deleting this collection');
  };

  handleDeleteCollectionCompleted = () => {
    const { segments = [] } = this.props;

    ToastsContainer.addSuccess('Collection deleted');

    segments.forEach((segment) => {
      trackSegmentDeleted(segment, segmentActionLocations.collectionsMenu);
    });
  };

  closedStateKey = () => `${this.props.collection.id}:closed`;

  getClosedState = () => !!window.localStorage.getItem(this.closedStateKey());

  setInitialClosedState = (isClosed) => {
    if (isClosed) {
      window.localStorage.setItem(this.closedStateKey(), true);
    } else {
      window.localStorage.removeItem(this.closedStateKey());
    }
  };

  select = (event) => {
    event.preventDefault();
    this.setState((prevState) => {
      return { selected: !prevState.selected };
    });
  };

  renderSegment = (segment, index) => {
    const { classes, segmentId, highlightedIndex, type } = this.props;

    const { search } = this.props;
    const selected = segment.id === segmentId;
    const highlighted = index === highlightedIndex;
    const searchWords = search ? search.toString().split(' ') : [];

    return (
      <div
        className={classnames(classes.segmentLinkContainer, {
          [classes.selectedSegment]: selected,
        })}
        key={segment.id}>
        <Link
          className={classnames(classes.segmentLink, {
            ['bg-gradient-r-x']: selected,
            [classes.segmentLinkHighlighted]: highlighted,
          })}
          to={paths.segment(segment.type, segment.id)}>
          <SegmentListIcon segment={segment} className={classes.segmentIcon} />
          <span className={classes.segmentLinkLabel}>
            <Highlighter
              searchWords={searchWords}
              autoEscape
              textToHighlight={segment.name}
              highlightTag={({ children }) => (
                <strong className={classes.highlightedTerm}>{children}</strong>
              )}
            />
          </span>
        </Link>

        <SegmentActionsMenu
          buttonComponent={
            <a
              className={classnames(classes.segmentMoreMenu, {
                [classes.segmentSelectedMoreMenu]: selected,
              })}
              href="#none">
              <MoreIcon />
            </a>
          }
          onClone={(clone) => {
            ToastsContainer.addSuccess('Segment cloned');
            history.push(paths.segmentEdit(clone.type, clone.id));
          }}
          onDelete={
            selected
              ? () => {
                  history.replace(paths.segments(segment.type));
                }
              : null
          }
          segment={segment}
          type={type}
          location={segmentActionLocations.collectionsMenu}
        />
      </div>
    );
  };

  renderTitle = () => {
    const { classes, collection, type } = this.props;
    const { selected } = this.state;
    const segmentSelected = this.segmentSelected();

    const className = classnames(
      {
        [classes.selectedCollectionLink]: selected || segmentSelected,
        [classes.disableHoverCursorPointer]: segmentSelected,
      },
      classes.collectionLink,
    );

    return (
      <>
        <a href="#none" onClick={this.select} className={className}>
          {selected || segmentSelected ? (
            <OpenFolderIcon className={classes.collectionIcon} />
          ) : (
            <FolderIcon className={classes.collectionIcon} />
          )}
          <span className={classes.collectionLinkLabel}>{collection.name}</span>
        </a>
        <MoreMenu
          className={classes.collectionMoreMenu}
          onRename={() => this.setState({ renaming: true })}
          onDelete={() =>
            this.setState({ showMenu: false, showDeleteModal: true })
          }
          type={type}
          collection={collection}
        />
      </>
    );
  };

  renderRenameInput = () => {
    const { classes, collection, type } = this.props;
    const { selected } = this.state;
    const segmentSelected = this.segmentSelected();

    return (
      <CKField className={classes.renameCollectionInput}>
        <CollectionName
          type={type}
          collection={collection}
          onBlur={() => {
            this.setState({ renaming: false });
          }}
        />
        <CKFieldLeftElement>
          {selected || segmentSelected ? (
            <OpenFolderIcon className={classes.renameCollectionIcon} />
          ) : (
            <FolderIcon className={classes.renameCollectionIcon} />
          )}
        </CKFieldLeftElement>
        <CKFieldRightElement isInteractive>
          <ClearIcon
            onClick={() => {
              this.setState({ renaming: false });
            }}
            width="16"
            height="16"
            className="text-white cursor-pointer opacity-40 hover:opacity-70 transition ease-linear duration-200"
          />
        </CKFieldRightElement>
      </CKField>
    );
  };

  render() {
    const { selected, renaming, showDeleteModal } = this.state;
    const {
      search,
      classes,
      highlightedIndex,
      segments,
      collection,
      canDelete,
      type,
    } = this.props;

    const segmentIsHighlighted = highlightedIndex > -1;
    const segmentSelected = this.segmentSelected();

    const showSegments =
      selected || segmentIsHighlighted || !!search || segmentSelected;

    return (
      <div
        ref={this.segmentsDiv}
        className={classnames(classes.collection, {
          [classes.openCollection]: selected,
        })}>
        <div className={classes.collectionLinkContainer}>
          {renaming ? this.renderRenameInput() : this.renderTitle()}
        </div>
        {showDeleteModal && (
          <Mutation
            mutation={destroyCollectionMutation}
            variables={{
              input: {
                type,
                id: collection.id,
              },
            }}
            onError={this.handleDeleteCollectionError}
            onCompleted={this.handleDeleteCollectionCompleted}
            refetchQueries={['SegmentsMenu', 'SegmentActivity']}>
            {(mutation) => (
              <ConfirmDeleteModal
                associatedItemNames={segments.map((segment) => segment.name)}
                open
                type="collection"
                item={collection}
                canDelete={canDelete}
                deleteItem={mutation}
                onCompleted={() => this.setState({ menuOpen: false })}
                closeModal={() => {
                  this.setState({
                    showDeleteModal: false,
                    menuOpen: false,
                  });
                }}
              />
            )}
          </Mutation>
        )}
        {showSegments && (
          <div className={classes.collectionSegments}>
            {segments.length > 0 ? (
              segments.map((segment, i) => this.renderSegment(segment, i))
            ) : (
              <div className={classes.noSegments}>No segments</div>
            )}
          </div>
        )}
      </div>
    );
  }
}

Collection.propTypes = {
  segments: PropTypes.array.isRequired,
  segmentId: PropTypes.string,
  collections: PropTypes.array,
  collection: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    segments: PropTypes.array,
    __typename: PropTypes.string.isRequired,
  }),
  classes: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['Company', 'Person']).isRequired,
  search: PropTypes.string.isRequired,
  highlightedIndex: PropTypes.number.isRequired,
  canDelete: PropTypes.bool.isRequired,
  addingSegment: PropTypes.bool,
};

export default withStyles(styles)(Collection);
